import { Ajax } from '@shared/Ajax';
import { Events } from '@shared/Events';
import { FileInfoDialog } from '@shared/files/ui/FileInfoDialog';
import { LogpointsFilterStore } from '@shared/logpoints/LogpointsFilterStore';
import { LogpointsService } from '@shared/logpoints/LogpointsService';
import { LogpointsFilter } from '@shared/logpoints/ui/LogpointsFilter';
import { MapPanelFilter } from '@shared/map/MapPanelFilter';
import { TreeSelectionFeedback } from '@shared/map/TreeSelectionFeedback';
import { FilesService } from '@shared/services/FilesService';
import { LStorage } from '@shared/Storage';
import { Tree, TreeNode } from '@shared/ui-native/Tree';
import { IContextMenuItem, renderContextMenuAtPosition } from '@shared/ui-react/ContextMenu';
import { TabPane, Tabs } from '@shared/ui-react/Tabs';
import { PageStateUtils } from '@shared/user-state/PageStateUtils';
import { DateUtils } from '@shared/utils/DateUtils';
import { CreateEditFolderDialog } from '@pages/files/dialogs/CreateEditFolderDialog';
import { Tree as TreeDS, type TreeNodeData, collectAllFolderIds } from 'design-system';
import { action, autorun, makeObservable, observable } from 'mobx';
import { observer } from 'mobx-react';
import * as React from 'react';
import { TreeToolbarResponsive } from '@shared/ui-react/TreeToolbar';

@observer
export class LogpointsPanelGenericComponent
    extends React.Component<{
        treeData: ITreeData | null;

        loadFolder: (id, treeNode: TreeNode) => JQueryPromise<{ features: any[]; failedToCreate: any[] }>;
        loadFile: (id, treeNode: TreeNode) => JQueryPromise<{ features: any[]; failedToCreate: any[] }>;
        loadLogpointsById: (ids: number[], treeNodes: TreeNode[]) => Promise<any>;

        clearAllLogpointFromMap: () => void;
        clearLogpointFromMapBySource: (source: FeatureSource) => void;
        zoomLogpointBySource: (source: FeatureSource) => void;

        getDisplaySettings?: () => ILogpointDisplaySettings;
        restoreDisplaySettings?: (settings: ILogpointDisplaySettings) => void;

        onFilterChanged: (state: ILogpointsFilter) => void;
        getAllLoadedLogpoints: () => ILogpoint[];

        pinLeftOffset?: number;
        mobileView?: boolean;
    }>
    implements IStateful<IMapPageStateLogpointsPanel>
{
    logpointsFilterStore: LogpointsFilterStore = null;
    tree: Tree = null;

    @observable isRendered = false;
    @observable private treeNodes: TreeNodeData[] = [];
    @observable private checkedIds: (number | string)[] = [];
    @observable private selectedId: number | string | null = null;
    @observable private folderCounts: Record<number, number> = {};
    @observable private modelCounts: Record<number, number> = {};
    @observable private childLogpointsExist: Record<number, number> = {};
    @observable private filteredFolderCounts: Record<number, number> = {};
    @observable private filteredModelCounts: Record<number, number> = {};
    @observable private filteredLogpointsCount = 0;
    @observable private totalLogpointsCount = 0;
    @observable private filterText = '';
    @observable private expandedIds: (number | string)[] = [];
    @observable private hideEmptyFolders = true;
    @observable private expandOnStart = 'firstLevel';

    totalCounts = {
        logpointsExistInSubFilesOrSubFolders: {},
        childLogpointsExist: {},
        folderCounts: {},
        modelCounts: {},
        totalCount: 0,
    };

    @observable onFilterChangedFn: (state: ILogpointsFilter) => void;
    @observable getAllLoadedLogpointsFn: () => ILogpoint[];

    static defaultProps = {
        activeTab: 'files',
    };

    constructor(props) {
        super(props);
        makeObservable(this);
        this.logpointsFilterStore = new LogpointsFilterStore(this.onFilterChanged);
        this.onFilterChangedFn = this.props.onFilterChanged;
        this.getAllLoadedLogpointsFn = this.props.getAllLoadedLogpoints;
    }

    componentDidMount() {
        this.loadedLogpointsChanged();
        if (this.props.treeData) {
            this.init();
        }
    }

    componentDidUpdate(prevProps: any) {
        if (prevProps.treeData !== this.props.treeData && !this.tree) {
            this.init();
        }
    }

    init = () => {
        this.initTree();

        this.tree.on('render', () => {
            setTimeout(() => {
                action(() => {
                    this.isRendered = true;
                    this.refreshNodes();
                })();
                this.renderSelectionFeedback();
                autorun(this.renderSelectionFeedback);
            });
        });
        this.tree.on(
            'check',
            action(() => {
                this.checkedIds = this.tree.getChecked().map(n => this.nodeToId(n));
            })
        );
        this.tree.on('check', this.onFilterChanged);
    };

    private initTree() {
        if (this.tree) return;

        this.tree = new Tree({
            renderView: false,
            showRoot: true,
            showCheckboxes: true,
            selectionCheckbox: false,
            propagateCheckbox: false,
            checkOnHighlightSelected: true,
            showContextMenuCreateFolder: true,
            showContextMenuInfo: true,
            hideEmptyFolders: true,
            hideEmptyFoldersAlways: this.props.mobileView,
            storageKey: 'map-logpoints-panel',
            fetchContentCountsFn: async () => {
                await this.renderLogpointFolderCounts(true); // will also fetch new counts
                setTimeout(() => {
                    // tree will render counts but it doesn't know about filteredCounts, so we need to render here again
                    this.renderLogpointFolderCounts(false);
                }, 1000);
                return {
                    ...this.totalCounts.folderCounts,
                    ...this.totalCounts.modelCounts,
                };
            },
            childFilter: c => {
                if (c.tolerance) return true; // show folders
                if (c.modelId) return false; // hide links
                // must include dxf, dwg - IN-5597
                return !FilesService.isOfficeDocument(c.name);
            },
        });

        this.tree.on('check', (e, data) => {
            data.changed.forEach((node: TreeNode) => {
                const id = node.id;
                // skip loading if counts = 0, but only if counts are loaded
                // restore page state might trigger load before counts are loaded and we dont want to skip
                if (Object.keys(this.tree.contentCounts).length && !this.tree.contentCounts[id]) {
                    return;
                }

                // checked folder
                if (node.isFolder()) {
                    if (node.state.checked == 1) {
                        // checked
                        node.set('loading', true);
                        this.props
                            .loadFolder(id, node)
                            .then(result => {
                                node.setError(
                                    result?.failedToCreate?.length > 0 ? __('js_logpoints.failed_to_load_number_because_bad_coords', result?.failedToCreate?.length) : null
                                );
                            })
                            .always(() => node.set('loading', false));
                    } else {
                        // unchecked
                        this.props.clearLogpointFromMapBySource({
                            folderId: node.id,
                            folderUuid: node.ajaxData.uuid,
                        });
                        node.setError(null);
                    }
                }

                // checked file
                else if (node.isFile() && (kuuraModel.isModel(node.ajaxData) || kuuraModel.isAlignment(node.ajaxData))) {
                    if (node.state.checked == 1) {
                        node.set('loading', true);
                        this.props
                            .loadFile(node.id, node)
                            .then(result => {
                                node.setError(
                                    result?.failedToCreate?.length > 0 ? __('js_logpoints.failed_to_load_number_because_bad_coords', result?.failedToCreate?.length) : null
                                );
                            })
                            .always(() => node.set('loading', false));
                    } else {
                        this.props.clearLogpointFromMapBySource({
                            modelId: node.id,
                            modelUuid: node.ajaxData.uuid,
                            alignmentId: node.id,
                        });
                        node.setError(null);
                    }
                }
            });
        });

        this.tree.on('render', () => {
            // needs to be done after render, because backend required list of folder ids to fetch counts
            this.renderLogpointFolderCounts(true);
            this.refreshNodes();

            if (this.stateful.queuedRestoreState) {
                this.stateful.restoreState(this.stateful.queuedRestoreState);
            }
        });

        this.tree.initAndRender(this.props.treeData, false /* dont treat them as files since they are just containers for logpoints like folders are*/);

        // Sync persisted settings from native tree to MobX store
        const storedState = this.tree.getStoredState();
        if (storedState?.expandOnStart) {
            this.expandOnStart = storedState.expandOnStart;
        }
        if (storedState?.hideEmptyFolders != null) {
            this.hideEmptyFolders = storedState.hideEmptyFolders;
        }

        this.tree.hideEmptyFolders = this.tree.hideEmptyFoldersAlways || this.hideEmptyFolders;

        const expandedDsIds: (number | string)[] = [];
        this.tree.each(c => {
            if (c.isFolder() && c.state.expanded) {
                expandedDsIds.push(this.nodeToId(c));
            }
        }, true);
        this.expandedIds = expandedDsIds;
    }

    // Namespace IDs to avoid collisions between folders and files that share the same numeric ID
    private nodeToId = (node: TreeNode): string => (node.isFolder() ? `folder-${node.id}` : `file-${node.id}`);

    private convertNode = (node: TreeNode): TreeNodeData => ({
        id: this.nodeToId(node),
        name: node.ajaxData?.name ?? node.text ?? '',
        type: node.isFolder() ? 'folder' : 'file',
        children: node.children?.filter(c => !c.state.hidden).map(this.convertNode),
        data: node,
    });

    @action private refreshNodes(): void {
        if (!this.tree) return;
        this.treeNodes = [this.tree].map(this.convertNode);
    }

    private handleContextMenu = (node: TreeNodeData, position: { x: number; y: number }) => {
        const items: IContextMenuItem[] = [];
        const nativeNode = node.data as TreeNode;
        const isMobilePage = window.location.href.includes('mobile');

        if (node.type === 'file' || node.type === 'link') {
            const isLink = node.type === 'link';
            const modelId = isLink ? nativeNode.ajaxData?.modelId : nativeNode.ajaxData?.id;
            const modelUuid = isLink ? nativeNode.ajaxData?.modelUuid : nativeNode.ajaxData?.uuid;
            const folderId = isLink ? nativeNode.ajaxData?.model?.folderId : nativeNode.ajaxData?.folderId;

            if (kuura.hasPrivilege('Download_Model')) {
                items.push({
                    label: __('general.download_file'),
                    icon: 'iki iki-download',
                    action: () => {
                        window.open(location.origin + kuura.getContextPath() + `/api/1/file/${modelId}/get`, '_blank')?.focus();
                    },
                });
            }

            if (!window.location.href.includes('models.htm') && !isMobilePage && kuura.hasPrivilege('MODEL_PAGE')) {
                items.push({
                    label: __('js_menu.open_in_files_page'),
                    icon: 'iki iki-external',
                    action: () => {
                        window.open(location.origin + kuura.getContextPath() + '/models.htm#/' + folderId + '/' + modelId, '_blank')?.focus();
                    },
                });
            }

            items.push({
                label: __('general.show_details'),
                icon: 'iki iki-info',
                action: () => FileInfoDialog.open(modelUuid, true, true),
            });
        } else if (node.type === 'folder') {
            if (!isMobilePage && !nativeNode.state?.readOnly) {
                items.push({
                    label: __('js_files.create_folder'),
                    icon: 'iki iki-folder-closed',
                    action: () => this.tree.openCreateFolder(nativeNode),
                });
            }

            items.push({
                label: __('general.show_details'),
                icon: 'iki iki-info',
                action: () => CreateEditFolderDialog.info(nativeNode),
            });
        }

        if (!items.length) return;

        Events.trigger('map-hide-overlays');
        renderContextMenuAtPosition(items, position.x, position.y, 'tree-context-menu', () => {});
    };

    render() {
        const store = this.logpointsFilterStore;

        if (this.props.treeData === null) return <div className='overlay-loading no-bg' style={{ height: '100%' }}></div>;

        return (
            <>
                <Tabs displayValue='flex' flexDirection='column'>
                    <TabPane title={__('general.folder_tree')}>
                        <div className='flex-row gap-half' style={{ alignItems: 'center' }}>
                            {this.isRendered && (
                                <div id='logpoints-tree-toolbar' style={{ flexGrow: 1, overflow: 'hidden' }}>
                                    <TreeToolbarResponsive
                                        filterText={this.filterText}
                                        onFilterChange={text => {
                                            this.filterText = text;
                                        }}
                                        onExpandAll={() => {
                                            this.expandedIds = Array.from(collectAllFolderIds(this.treeNodes));
                                        }}
                                        onCollapseAll={() => {
                                            this.expandedIds = [];
                                        }}
                                        hideEmptyFolders={this.hideEmptyFolders}
                                        hideEmptyFoldersAlways={this.props.mobileView}
                                        onToggleHideEmptyFolders={() => {
                                            this.hideEmptyFolders = !this.hideEmptyFolders;
                                            this.tree?.toggleHideEmptyFolders();
                                            setTimeout(() => this.refreshNodes(), 50);
                                        }}
                                        expandOnStart={this.expandOnStart}
                                        onSetExpandOnStart={value => {
                                            this.expandOnStart = value;
                                            this.tree?.setExpandOnStart(value as any);
                                        }}
                                        showContextMenuCreateFolder={true}
                                        onOpenCreateFolder={() => {
                                            this.tree?.openCreateFolder(this.tree.getSelected() ?? this.tree.root);
                                        }}
                                        hasContentCounts={true}
                                    />
                                </div>
                            )}
                            {this.isRendered && (
                                <MapPanelFilter
                                    pinned={this.props.mobileView ? false : store.filterPinned}
                                    pinLeftOffset={this.props.pinLeftOffset}
                                    opened={store.filterOpened}
                                    activeFiltersCount={store.numberOfAppliedFilters}
                                    clearFilter={store.clearFilter}
                                    togglePinned={
                                        this.props.mobileView
                                            ? null
                                            : () => {
                                                  store.filterPinned = !store.filterPinned;
                                                  store.filterOpened = true;
                                              }
                                    }
                                    toggleOpened={() => (store.filterOpened = !store.filterOpened)}
                                >
                                    <LogpointsFilter store={store} />
                                </MapPanelFilter>
                            )}
                        </div>

                        {this.isRendered && (
                            <TreeSelectionFeedback
                                checkedCount={this.checkedIds.length}
                                loadedItemsCount={this.filteredLogpointsCount}
                                totalItemsCount={this.totalLogpointsCount}
                                onClearSelection={this.selectNone}
                                onSelectAll={this.selectAll}
                                countTooltip={__('js_map.logpoints.selected_count_tooltip')}
                            />
                        )}

                        {this.treeNodes.length > 0 && (
                            <div style={{ overflow: 'auto', flexGrow: 1 }}>
                                <TreeDS
                                    nodes={this.treeNodes}
                                    showRoot={true}
                                    checkable={true}
                                    checkedIds={this.checkedIds}
                                    selectedId={this.selectedId}
                                    expandedIds={this.expandedIds}
                                    onExpandedIdsChange={ids => {
                                        this.expandedIds = ids;
                                    }}
                                    expandOnStart='firstLevel'
                                    hideEmptyFolders={this.hideEmptyFolders}
                                    sortChildren={true}
                                    propagateCheckbox={false}
                                    filterText={this.filterText}
                                    renderLabel={node => {
                                        const legacyId = (node.data as TreeNode).id as number;
                                        const isFolder = node.type === 'folder';
                                        const totalCount = isFolder ? this.folderCounts[legacyId] : this.modelCounts[legacyId];
                                        const filteredCount = isFolder ? this.filteredFolderCounts[legacyId] : this.filteredModelCounts[legacyId];
                                        const childExists = this.childLogpointsExist[legacyId];

                                        let countText = '';
                                        if (totalCount > 0) {
                                            countText += filteredCount != null ? `(${filteredCount ?? 0}/${totalCount})` : `(${totalCount})`;
                                        }
                                        if (childExists === 1) {
                                            countText += ` (...)`;
                                        }

                                        if (!countText) return node.name;
                                        return (
                                            <>
                                                {node.name} <span style={{ color: 'var(--ik-color-text-weakest)' }}>{countText}</span>
                                            </>
                                        );
                                    }}
                                    onNodeSelected={node => {
                                        action(() => {
                                            this.selectedId = node.id;
                                        })();
                                        const legacyNode = node.data as TreeNode;
                                        if (legacyNode && typeof this.props.zoomLogpointBySource === 'function') {
                                            const source: FeatureSource = {};
                                            if (legacyNode.isFolder()) {
                                                source.folderId = legacyNode.id as number;
                                                source.folderUuid = legacyNode.ajaxData?.uuid;
                                            } else {
                                                source.modelId = legacyNode.id as number;
                                                source.modelUuid = legacyNode.ajaxData?.modelUuid;
                                            }
                                            this.props.zoomLogpointBySource(source);
                                        }
                                    }}
                                    onNodeChecked={node => {
                                        (node.data as TreeNode)?.setChecked(1);
                                    }}
                                    onNodeUnchecked={node => {
                                        (node.data as TreeNode)?.setChecked(0);
                                    }}
                                    onContextMenu={this.handleContextMenu}
                                />
                            </div>
                        )}
                    </TabPane>
                </Tabs>
            </>
        );
    }

    selectAll = () => {
        const loadAllLogpoints = () => {
            this.tree.setChecked(1, false, 'deep', true);
        };

        const countThreshold = 1000;
        Ajax({
            url: 'api/1/logpoint/counts_for_project.json',
        }).done(resp => {
            setTimeout(() => {
                if (resp.count > countThreshold) {
                    window.DialogConfirm({ msg: kuura.getMessage('js_map.logpoints.load_too_many', resp.count) }, () => {
                        loadAllLogpoints();
                    });
                } else {
                    loadAllLogpoints();
                }
            }, 100);
        });
    };

    @action
    selectNone = () => {
        if (this.tree) {
            this.tree.each(
                c => {
                    c.setChecked(0, true);
                },
                true,
                true
            );
            this.checkedIds = [];
            this.props.clearAllLogpointFromMap();
            this.renderSelectionFeedback();
        }
        LStorage.removeItem('map-show-logpoints-filter');
    };

    onFilterChanged = () => {
        this.renderSelectionFeedback();
        this.renderLogpointFolderCounts(false);
        if (this.tree) {
            this.tree.calculateCheckboxes(true);
        }
        this.onFilterChangedFn(this.logpointsFilterStore.filter);
    };

    loadedLogpointsChanged = _.debounce(() => {
        this.logpointsFilterStore.updateLoadedCodes(this.getAllLoadedLogpointsFn());
        this.renderSelectionFeedback();
        this.renderLogpointFolderCounts(false);
    }, 50);

    renderLogpointFolderCounts = async (fetchNew = false) => {
        if (!this.tree) return;

        const folderIdArray = [];
        this.tree.each(
            c => {
                if (c.isFolder()) folderIdArray.push(c.id);
            },
            true,
            true
        );

        const filteredCounts = this.getFilteredCounts();

        if (fetchNew) {
            await LogpointsService.fetchFolderCounts(folderIdArray).then(counts => {
                this.totalCounts = counts;
            });
        }

        action(() => {
            this.folderCounts = { ...this.totalCounts.folderCounts } as Record<number, number>;
            this.modelCounts = { ...this.totalCounts.modelCounts } as Record<number, number>;
            this.childLogpointsExist = { ...this.totalCounts.childLogpointsExist } as Record<number, number>;
            this.filteredFolderCounts = { ...filteredCounts.folders } as Record<number, number>;
            this.filteredModelCounts = { ...filteredCounts.models } as Record<number, number>;
        })();
    };

    getFilteredCounts() {
        const allFiltered = this.getAllLoadedLogpointsFn().filter(l => LogpointsService.matchesFilter(l, this.logpointsFilterStore.filter));

        const folders = _.countBy(allFiltered, f => f.folderId);
        const models = _.countBy(allFiltered, f => f.modelId || f.alignmentId);

        return {
            folders,
            models,
        };
    }

    private renderSelectionFeedback = _.debounce(
        action(() => {
            if (!this.isRendered) return;
            const allFiltered = this.getAllLoadedLogpointsFn().filter(l => LogpointsService.matchesFilter(l, this.logpointsFilterStore.filter));
            this.filteredLogpointsCount = allFiltered.length;
            this.totalLogpointsCount = this.totalCounts.totalCount || 0;
        }),
        100
    );

    getStoredLogpointsFilter = (): string | null => {
        const selectionRaw = LStorage.getItem('map-show-logpoints-filter');
        const selection = JSON.parse(selectionRaw);
        if (selection?.timestamp) {
            if (Date.now() - selection.timestamp > 3600000) {
                // clear when older than 1h
                LStorage.removeItem('map-show-logpoints-filter');
            } else if (selection.projectId === kuura.getProjectId()) {
                return selection.items;
            }
        }
        return null;
    };

    stateful = {
        queuedRestoreState: null,

        defaultState: {
            checkedFolderIds: [],
            checkedFileIds: [],
            timestamp: {
                from: null,
                to: null,
                preset: null,
            },
            userIds: null,
            codes: null,
            vehicleIds: null,
            sources: null,
            approvals: null,
            onlyOutOfTolerance: false,
            iconLabelProp: 'dz',
            showLines: true,
            noNamesInLabel: false,
            colorFunction: 'tolerance_approval' as LogpointColorFunction,
            isFiltersPanelCollapsed: false,
            hideDzPlusMinus: null,
            hideDzMode: 'inside' as 'inside' | 'outside',
            logpoints: undefined,
        },

        restoreDefaultState: (): void => {
            this.tree?.checkIds([], true);
            this.stateful.restoreState(this.stateful.defaultState);
            // logpoints can be loaded individually outside of file tree, this clears them
            if (typeof this.props.clearAllLogpointFromMap === 'function') {
                this.props.clearAllLogpointFromMap();
            }
        },

        getCurrentState: (): IMapPageStateLogpointsPanel => {
            if (!this.tree) return null;

            const { checkedIds: checkedFolderIds } = this.tree.getCheckedAndUncheckedFolderIds();
            const { checkedIds: checkedFileIds } = this.tree.getCheckedAndUncheckedFileIds();

            let storageLogpointIds = this.getStoredLogpointsFilter();
            const displaySettings = this.props.getDisplaySettings ? this.props.getDisplaySettings() : {};

            return {
                checkedFolderIds,
                checkedFileIds,
                timestamp: {
                    from: this.logpointsFilterStore.filter.fromTimestamp,
                    to: this.logpointsFilterStore.filter.toTimestamp,
                    preset: this.logpointsFilterStore.filter.datePresetIndex,
                },
                userIds: this.logpointsFilterStore.filter.userIds,
                codes: this.logpointsFilterStore.filter.codes,
                vehicleIds: this.logpointsFilterStore.filter.vehicleIds,
                sources: this.logpointsFilterStore.filter.sources,
                approvals: this.logpointsFilterStore.filter.approvals,
                onlyOutOfTolerance: this.logpointsFilterStore.filter.onlyOutOfTolerance,
                hideDzPlusMinus: this.logpointsFilterStore.filter.hideDzPlusMinus,
                hideDzMode: this.logpointsFilterStore.filter.hideDzMode,
                logpointIds: storageLogpointIds,
                ...displaySettings,
            };
        },

        restoreState: (state: IMapPageStateLogpointsPanel, liveState?: boolean): void => {
            if (!state) return null;

            if (!this.tree) {
                this.stateful.queuedRestoreState = JSON.parse(JSON.stringify(state)) as IMapPageStateModelsPanel;
                return;
            }

            if (PageStateUtils.stateHasKey<IMapPageStateLogpointsPanel>(state, 'timestamp')) {
                if (state.timestamp.hasOwnProperty('preset') && state.timestamp.preset >= 0 && DateUtils.presets[state.timestamp.preset]) {
                    const preset = DateUtils.presets[state.timestamp.preset];
                    this.logpointsFilterStore.setDates(preset.from, preset.to, state.timestamp.preset);
                } else {
                    this.logpointsFilterStore.setDates(state.timestamp.from, state.timestamp.to, null);
                }
            }

            if (PageStateUtils.stateHasKey<IMapPageStateLogpointsPanel>(state, 'onlyOutOfTolerance')) {
                this.logpointsFilterStore.filter.onlyOutOfTolerance = state.onlyOutOfTolerance;
            }

            if (PageStateUtils.stateHasKey<IMapPageStateLogpointsPanel>(state, 'hideDzPlusMinus')) {
                this.logpointsFilterStore.filter.hideDzPlusMinus = state.hideDzPlusMinus;
            }

            if (PageStateUtils.stateHasKey<IMapPageStateLogpointsPanel>(state, 'hideDzMode')) {
                this.logpointsFilterStore.filter.hideDzMode = state.hideDzMode;
            }

            if (typeof this.props.restoreDisplaySettings === 'function') {
                this.props.restoreDisplaySettings(state);
            }

            // load individual logpoints by ids
            if (!liveState) {
                if (!this.getStoredLogpointsFilter()?.length /* dont clear when loaded from url */) {
                    this.selectNone();

                    if (typeof this.props.clearAllLogpointFromMap === 'function') {
                        this.props.clearAllLogpointFromMap();
                    }
                }

                if (PageStateUtils.stateHasKey<IMapPageStateLogpointsPanel>(state, 'logpointIds')) {
                    const logpointListIds = state.logpointIds?.length ? state.logpointIds?.split(',').map(Number) : [];
                    if (
                        logpointListIds?.length &&
                        !window.location.href.includes('show-logpoints-by-filter') && // url-params.ts has already loaded logpoints
                        typeof this.props.loadLogpointsById === 'function'
                    ) {
                        this.props.loadLogpointsById(logpointListIds, null);
                    }
                }
            }

            if (
                PageStateUtils.stateHasKey<IMapPageStateLogpointsPanel>(state, 'checkedFolderIds') ||
                PageStateUtils.stateHasKey<IMapPageStateLogpointsPanel>(state, 'checkedFileIds')
            ) {
                const foldersAndFiles = [...state.checkedFolderIds, ...state.checkedFileIds];

                if (PageStateUtils.stateHasKey<IMapPageStateLogpointsPanel>(state, 'codes')) {
                    this.logpointsFilterStore.filter.codes = (state.codes ?? []).filter(Boolean).filter(code => code !== 'null');
                } else if (PageStateUtils.stateHasKey<IMapPageStateLogpointsPanel>(state, 'code') && state.code && state.code !== 'null') {
                    this.logpointsFilterStore.filter.codes = [state.code];
                }

                this.tree.checkIds(foldersAndFiles, true, false);
                this.tree.expandIds(foldersAndFiles);
            }

            if (
                PageStateUtils.stateHasKey<IMapPageStateLogpointsPanel>(state, 'approval') &&
                (['MEASURED', 'CHECKED', 'APPROVED'] as LogpointApprovalStage[]).includes(state.approval)
            ) {
                this.logpointsFilterStore.filter.approvals = [state.approval];
            }

            if (PageStateUtils.stateHasKey<IMapPageStateLogpointsPanel>(state, 'approvals')) {
                this.logpointsFilterStore.filter.approvals = state.approvals;
            }

            if (PageStateUtils.stateHasKey<IMapPageStateLogpointsPanel>(state, 'sources')) {
                this.logpointsFilterStore.filter.sources = (state.sources ?? []).filter(Boolean);
            } else {
                if (PageStateUtils.stateHasKey<IMapPageStateLogpointsPanel>(state, 'source') && state.source) {
                    this.logpointsFilterStore.filter.sources = [state.source];
                }
            }

            if (PageStateUtils.stateHasKey<IMapPageStateLogpointsPanel>(state, 'userIds')) {
                this.logpointsFilterStore.filter.userIds = (state.userIds ?? []).filter(Boolean);
            } else {
                if (PageStateUtils.stateHasKey<IMapPageStateLogpointsPanel>(state, 'userId') && state.userId) {
                    this.logpointsFilterStore.filter.userIds = [parseInt(state.userId)].filter(Boolean);
                }
            }

            if (PageStateUtils.stateHasKey<IMapPageStateLogpointsPanel>(state, 'vehicleIds')) {
                this.logpointsFilterStore.filter.vehicleIds = (state.vehicleIds ?? []).filter(Boolean);
            } else if (PageStateUtils.stateHasKey<IMapPageStateLogpointsPanel>(state, 'vehicleId') && state.vehicleId) {
                this.logpointsFilterStore.filter.vehicleIds = [state.vehicleId];
            } else if (PageStateUtils.stateHasKey<IMapPageStateLogpointsPanel>(state, 'vehicle') && state.vehicle) {
                // legacy, vehicle name or source/type name
                if (['ROVER_GPS', 'TOTAL_STATION', 'MANUAL'].includes(state.vehicle.toUpperCase())) {
                    this.logpointsFilterStore.filter.sources = [state.vehicle.toUpperCase() as LogpointSource];
                } else {
                    if (this.logpointsFilterStore.vehicles.size > 0) {
                        const found = [...this.logpointsFilterStore.vehicles.values()].find(v => v.title === state.vehicle);
                        if (found) {
                            this.logpointsFilterStore.filter.vehicleIds = [found.value as number];
                        }
                    } else {
                        // vehicles filter not yet loaded
                        this.logpointsFilterStore.initialVehicleName = state.vehicle;
                    }
                }
            }

            this.stateful.queuedRestoreState = null;
        },
    };
}
